/**
 * @description TDC 统一的 promotionType 枚举和判断函数
 * @see TDC/apps/api/src/modules/marketing/promotion/promotion.constants.ts
 */

/**
 * @description 活动类型常量（与 TDC 后端一致）
 */
export const PROMOTION_TYPE = {
  NONE: 0, // 无活动（普通商品）
  VOLUME_PRICE: 1, // 满量特价（自动匹配，不存储）
  VOLUME_DISCOUNT: 2, // 满量折扣（原"满减/限时折扣"）
  BUY_GIFT: 3, // 买赠
  COMBO_BUNDLE: 4, // 搭配套餐（原"套餐"，有主商品概念）
  COMBO_PROMO: 5, // 组合促销（新增，有阶梯，无主商品概念）
}

/**
 * @description 旧版 promotionType 到新版映射（兼容历史数据）
 * - 旧1（秒杀）已废弃
 * - 旧2（满减折扣）→ 新2（满量折扣）
 * - 旧3（套餐）→ 新4（搭配套餐）
 * - 旧4（买赠）→ 新3（买赠）
 */
export const LEGACY_PROMOTION_TYPE_MAP = {
  1: null, // 秒杀已废弃
  2: PROMOTION_TYPE.VOLUME_DISCOUNT,
  3: PROMOTION_TYPE.COMBO_BUNDLE,
  4: PROMOTION_TYPE.BUY_GIFT,
}

/**
 * @description 将旧版 promotionType 映射到新版
 * @param {number} legacyType - 旧版活动类型
 * @returns {number|null} 新版活动类型，废弃类型返回 null
 */
export function mapLegacyPromotionType(legacyType) {
  const n = Number(legacyType)
  if (!Number.isFinite(n) || n < 0) return null
  if (n === 0) return PROMOTION_TYPE.NONE
  if (n >= 1 && n <= 5) return n // 新版直接返回
  return LEGACY_PROMOTION_TYPE_MAP[n] ?? null
}

/**
 * @description 判断是否无活动（普通商品）
 */
export function isNoPromotion(pt) {
  return Number(pt) === PROMOTION_TYPE.NONE || !Number(pt)
}

/**
 * @description 判断是否满量特价（type=1，自动匹配）
 */
export function isVolumePrice(pt) {
  return Number(pt) === PROMOTION_TYPE.VOLUME_PRICE
}

/**
 * @description 判断是否满量折扣（type=2）
 */
export function isVolumeDiscount(pt) {
  return Number(pt) === PROMOTION_TYPE.VOLUME_DISCOUNT
}

/**
 * @description 判断是否买赠（type=3）
 */
export function isBuyGift(pt) {
  return Number(pt) === PROMOTION_TYPE.BUY_GIFT
}

/**
 * @description 判断是否搭配套餐（type=4）
 */
export function isComboBundle(pt) {
  return Number(pt) === PROMOTION_TYPE.COMBO_BUNDLE
}

/**
 * @description 判断是否组合促销（type=5）
 */
export function isComboPromo(pt) {
  return Number(pt) === PROMOTION_TYPE.COMBO_PROMO
}

/**
 * @description 判断是否套餐类活动（搭配套餐或组合促销）
 */
export function isComboPackage(pt) {
  const n = Number(pt)
  return n === PROMOTION_TYPE.COMBO_BUNDLE || n === PROMOTION_TYPE.COMBO_PROMO
}

/**
 * @description 判断是否有活动（排除普通商品）
 * - 满量特价(1)虽然不存储，但在展示时仍视为有活动
 */
export function hasPromotion(pt) {
  const n = Number(pt)
  return n > PROMOTION_TYPE.NONE && n <= PROMOTION_TYPE.COMBO_PROMO
}

/**
 * @description 判断是否需要存储的活动类型（套餐/组合）
 * - 满量特价(1)、满量折扣(2)、买赠(3)不存储，动态匹配
 * - 搭配套餐(4)、组合促销(5)需要存储 comboKey
 */
export function isStoragePromotion(pt) {
  return isComboPackage(pt)
}

/**
 * @description 判断是否自动匹配的活动类型（不存储）
 * - 满量特价(1)、满量折扣(2)、买赠(3)由后端在查询时自动匹配
 */
export function isAutoMatchPromotion(pt) {
  const n = Number(pt)
  return (
    n === PROMOTION_TYPE.VOLUME_PRICE ||
    n === PROMOTION_TYPE.VOLUME_DISCOUNT ||
    n === PROMOTION_TYPE.BUY_GIFT
  )
}

/**
 * @description 获取活动类型名称
 * @param {number} pt - 活动类型
 * @returns {string} 类型名称
 */
export function getPromotionTypeName(pt) {
  const n = Number(pt)
  switch (n) {
    case PROMOTION_TYPE.NONE:
      return '普通商品'
    case PROMOTION_TYPE.VOLUME_PRICE:
      return '满量特价'
    case PROMOTION_TYPE.VOLUME_DISCOUNT:
      return '满量折扣'
    case PROMOTION_TYPE.BUY_GIFT:
      return '买赠'
    case PROMOTION_TYPE.COMBO_BUNDLE:
      return '搭配套餐'
    case PROMOTION_TYPE.COMBO_PROMO:
      return '组合促销'
    default:
      return '未知活动'
  }
}